import React from 'react';
import { Tag, Tooltip } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { FONT_SIZE, FONT_WEIGHT, SPACING } from '../../constants/constants';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { storeLabel } from '../../constants/stores';
// 의미 4종 + 중립 톤은 StatusBadge 와 같은 표를 쓴다(DS-1 — AppTag 는 그대로 쓰고 색만 한 곳으로, 결정 D9).
import { toneTheme } from './StatusBadge';

// ─── 타입 ─────────────────────────────────────────────────────────────────────

// Naver productStatusTypes 실제 값(NaverCommerceClient.ALL_PRODUCT_STATUS_TYPES) 전부 +
// 로컬에서만 쓰는 두 가지(DELETED/PROHIBITED, 아래 주석 참고).
type NaverSaleStatus =
  | 'WAIT' | 'SALE' | 'OUTOFSTOCK' | 'UNADMISSION' | 'REJECTION' | 'SUSPENSION' | 'CLOSE'
  | 'DELETED' | 'PROHIBITED';

const SALE_CONFIG: Record<NaverSaleStatus, { label: string; paletteKey: string; hint?: string }> = {
  WAIT:        { label: '판매대기',   paletteKey: 'saleWait'        },
  SALE:        { label: '판매중',     paletteKey: 'saleActive'      },
  OUTOFSTOCK:  { label: '품절',       paletteKey: 'saleOutOfStock'  },
  UNADMISSION: { label: '승인대기',   paletteKey: 'saleUnadmission' },
  REJECTION:   { label: '승인거부',   paletteKey: 'saleRejection'   },
  SUSPENSION:  { label: '판매중지',   paletteKey: 'saleSuspension'  },
  CLOSE:       { label: '판매종료',   paletteKey: 'saleClose'       },
  // 스토어 전체 카탈로그 검색에서도 못 찾음 = 완전 삭제(재검색 복구 불가). SYNC_STOCK이 반영.
  DELETED: {
    label: '스토어 미등록', paletteKey: 'saleUnknown',
    hint: '네이버 스토어 전체 검색에서도 이 상품을 찾을 수 없습니다 — 완전히 삭제된 것으로 추정되며, 재검색으로 복구되지 않습니다.',
  },
  // 스토어에서 판매금지(PROHIBITION) 상태라 내용 수정도 삭제도 API로 불가 — 로컬에만 코드를
  // 만들어 등록해두고 "영구히 못 고침"을 구분 표시한다(BackfillSellerCodeJob, 2026-07-27).
  PROHIBITED: {
    label: '판매금지(수정불가)', paletteKey: 'saleUnknown',
    hint: '네이버가 이 상품을 판매금지 처리해, 가격·재고 수정도 삭제도 이 화면(API)에서는 불가능합니다 — "스토어 미등록"과 달리 삭제된 게 아니라 막혀 있는 상태입니다.',
  },
};

/** SaleStatusBar 등에서 판매상태를 항상 같은 순서·구성으로 보여줄 때 쓰는 고정 목록. */
export const SALE_STATUS_ORDER: string[] = Object.keys(SALE_CONFIG);

/**
 * Naver 원본값 'PROHIBITION'과 우리 로컬 표준값 'PROHIBITED'는 완전히 같은 상태
 * (판매금지·가격/재고/코드 수정 불가). 백엔드가 'PROHIBITED'로 정규화하지만, 동기화
 * 전 데이터가 잠깐 'PROHIBITION'으로 올 수 있어 화면에서도 하나로 취급한다.
 */
export const canonicalSaleStatus = (status: string): string =>
  status === 'PROHIBITION' ? 'PROHIBITED' : status;

/** 판매상태 필터 드롭다운 등 Tag 밖에서도 같은 한글 라벨이 필요할 때 재사용. */
export const saleStatusLabel = (status: string): string =>
  SALE_CONFIG[canonicalSaleStatus(status) as NaverSaleStatus]?.label ?? status;

/** 판매상태별 색을 Tag 밖(예: SaleStatusBar의 pill)에서도 가져와야 할 때 재사용. t.tag(key)에 넘긴다. */
export const saleStatusPaletteKey = (status: string): string =>
  SALE_CONFIG[canonicalSaleStatus(status) as NaverSaleStatus]?.paletteKey ?? 'saleUnknown';

export type AppTagProps =
  | { variant: 'role';         isAdmin: boolean }
  | { variant: 'store';        code: string | null }
  | { variant: 'sale';         status: string }
  | { variant: 'manual' }
  | { variant: 'needsReview' }
  | { variant: 'rarity';       code: string | null | undefined }
  | { variant: 'registerStatus'; created: boolean }
  | { variant: 'jobResult';    type: 'success' | 'error' | 'dryRun'; count: number }
  | { variant: 'syncResult';   success: boolean; errorMessage?: string | null };

const baseStyle: React.CSSProperties = {
  fontSize: FONT_SIZE.xs,
  margin: 0,
  padding: `0 ${SPACING.xxs}px`,
};

// 토큰 TagTheme 를 Tag style 로 변환. theme 인자와 module-level baseStyle에만 의존하므로
// 컴포넌트 밖에 둔다 — AppTag는 모든 테이블 셀에서 행마다 렌더된다.
const themed = (theme: { bg: string; text: string; border: string }): React.CSSProperties => ({
  ...baseStyle,
  background: theme.bg,
  color: theme.text,
  borderColor: theme.border,
});

// ─── AppTag ───────────────────────────────────────────────────────────────────

/**
 * 프로젝트 공통 태그.
 * 색상은 전부 디자인 시스템 토큰(useThemeTokens)에서 가져온다.
 * Ant 의 color 프리셋(blue/orange/success 등)은 사용하지 않는다.
 */
export const AppTag: React.FC<AppTagProps> = (props) => {
  const t = useThemeTokens();

  switch (props.variant) {

    case 'role': {
      const theme = props.isAdmin
        ? toneTheme(t, 'info')
        : toneTheme(t, 'neutral');
      return <Tag style={themed(theme)}>{props.isAdmin ? '관리자' : '조회 전용'}</Tag>;
    }

    // 헤더 우측에서 지금 로그인/조회 중인 매장이 부산인지 광주인지 한눈에 보이게(2026-08-19,
    // "관리자" 태그만 있어 매장 구분이 안 된다는 피드백). code가 null이면 SUPER가 특정 매장을
    // 안 고르고 전 매장 개요를 보는 중 — 사이드바 SuperStoreSwitcher와 같은 라벨을 쓴다.
    case 'store':
      return (
        <Tag style={themed({ bg: t.bgRaised, text: t.textPrimary, border: t.border })}>
          {props.code ? storeLabel(props.code) : '전 매장 개요'}
        </Tag>
      );

    case 'sale': {
      const cfg = SALE_CONFIG[canonicalSaleStatus(props.status) as NaverSaleStatus];
      const theme = t.tag(cfg ? cfg.paletteKey : 'saleUnknown');
      const tag = <Tag style={themed(theme)}>{cfg?.label ?? props.status}</Tag>;
      // "스토어 미등록"/"판매금지(수정불가)"는 이름만 보면 둘 다 "문제 생김"으로 보여
      // 헷갈린다는 피드백(2026-08-07) — hover로 정확한 차이를 짚어준다.
      return cfg?.hint ? <Tooltip title={cfg.hint}>{tag}</Tooltip> : tag;
    }

    // 반영 로그처럼 행마다 성공/실패만 표시하는 태그 — naverSuccess · naverError 팔레트에 라벨을 "성공"/"실패"로 고정.
    // (옛 CardTable "Naver 반영" 열 전용이던 naverSuccess · naverError 변형은 쓰는 곳이 없어 지웠다 — 감사 card-controltower#703.)
    case 'syncResult':
      return props.success
        ? <Tag icon={<CheckCircleOutlined />} style={themed(t.tag('naverSuccess'))}>성공</Tag>
        : (
          <Tag icon={<CloseCircleOutlined />} title={props.errorMessage ?? undefined} style={themed(t.tag('naverError'))}>
            실패
          </Tag>
        );

    case 'manual':
      return (
        <Tag style={themed(toneTheme(t, 'warning'))}>
          수동
        </Tag>
      );

    // ImportProductsJob이 판매자상품코드 중복을 자동으로 임시 코드(-DUP) 부여·Naver
    // 반영까지 마쳤어도, 그 코드는 사람이 알아보기 어려운 값이라 검토가 필요한 카드 표시
    // (2026-08-20) — manual과 같은 주황 팔레트지만 "직접 반영이 아니라 확인 대기 상태"임을
    // 구분하도록 WarningOutlined 아이콘을 붙인다(jobResult의 dryRun과 같은 근거).
    case 'needsReview':
      return (
        <Tag icon={<WarningOutlined />} style={themed(toneTheme(t, 'warning'))}>
          코드 확인 필요
        </Tag>
      );

    // 상품 등록 화면 — SAR/UR/SR/RR/AR/ACE/C를 색으로 구분(2026-08-31, "상품 등록 개선안";
    // ACE=ACE SPEC는 2026-09-01 추가).
    // 레이아웃은 다른 태그와 동일하고 색·라벨만 다르므로 별도 컴포넌트가 아니라 variant로
    // 둔다(DESIGN_SYSTEM.md §5-4). minWidth는 표에서 한 글자(C)~세 글자(SAR) 등급이
    // 세로로 줄맞춤되도록 고정한다.
    case 'rarity': {
      if (!props.code) return <span style={{ color: t.textEmpty }}>-</span>;
      return (
        <Tag style={{
          ...themed(t.rarity(props.code)),
          fontWeight: FONT_WEIGHT.bold, minWidth: 34, textAlign: 'center',
        }}>
          {props.code}
        </Tag>
      );
    }

    // 상품 등록 미리보기 표의 "새로 생성 / 이미 등록됨 — 건너뜀"(2026-08-31).
    case 'registerStatus':
      return props.created
        ? <Tag style={themed(toneTheme(t, 'info'))}>새로 생성</Tag>
        : <Tag style={themed(t.tag('resultUnchanged'))}>이미 등록됨 — 건너뜀</Tag>;

    case 'jobResult': {
      // dryRun(naver.commerce.register-enabled=false로 실제 등록 없이 끝난 경우)을
      // success와 같은 초록으로 보여주면 진짜 성공과 구분이 안 된다(2026-08-19,
      // NAVER_REGISTER_ENABLED 환경변수 누락 사고 이후 추가) — manual 태그와 같은
      // 주황 팔레트 재사용.
      const theme = props.type === 'success'
        ? t.tag('resultIncrease')   // 성공 → 초록 계열
        : props.type === 'dryRun'
        ? toneTheme(t, 'warning') // dry-run → 주황 계열
        : toneTheme(t, 'danger');  // 실패 → 빨강 계열
      const icon = props.type === 'success'
        ? <CheckCircleOutlined />
        : props.type === 'dryRun'
        ? <WarningOutlined />
        : <CloseCircleOutlined />;
      const label = props.type === 'success' ? '성공' : props.type === 'dryRun' ? 'DRY-RUN' : '실패';
      return (
        <Tag icon={icon} style={themed(theme)}>
          {label} {props.count}건
        </Tag>
      );
    }
  }
};